import { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import {
  DOCUMENTS_PAGE_SIZE,
  DOCUMENTS_QUERY_KEY,
  DOCUMENTS_QUERY_OPTIONS,
} from '@/constant';
import { documentsApi } from '@/services/documents';
import { DocumentationDocument, DocumentListType } from '@/types/documents';

const hasProcessingDocument = (
  documents: DocumentationDocument[] | undefined
): boolean => {
  if (!documents) return false;

  return documents.some(doc => doc.status === 'processing');
};

export const useDocuments = (
  listType: DocumentListType,
  enabled: boolean = true
) => {
  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<
    | {
        documents: DocumentationDocument[];
        pagination?: {
          pageNumber: number;
          pageSize: number;
          pageItemCount: number;
        };
      }
    | undefined
  >({
    queryKey: [DOCUMENTS_QUERY_KEY, listType],
    queryFn: async ({ pageParam = 1 }) => {
      const context = {
        pageParam: pageParam as number,
        pageSize: DOCUMENTS_PAGE_SIZE,
      };
      const data =
        listType === DocumentListType.MyDocuments
          ? await documentsApi.listMyDocuments(context)
          : await documentsApi.listSharedDocuments(context);

      return (
        data || {
          documents: [],
        }
      );
    },
    getNextPageParam: lastPage => {
      if (!lastPage?.pagination) return undefined;
      const { pageNumber, pageItemCount } = lastPage.pagination;
      // If the current page has items and we got a full page, there might be more pages
      // Check if pageItemCount equals or exceeds the page size to determine if there are more pages
      // If pageItemCount is less than page size, we've reached the last page
      if (pageItemCount > 0 && pageItemCount >= DOCUMENTS_PAGE_SIZE) {
        return pageNumber + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled,
    ...DOCUMENTS_QUERY_OPTIONS,
    refetchInterval: query => {
      // Flatten all documents from all pages to check for processing documents
      const allDocuments = query.state.data?.pages.flatMap(
        page => page?.documents ?? []
      );
      // Refetch every 10 seconds while any document is still processing
      return hasProcessingDocument(allDocuments) ? 10000 : false;
    },
  });

  // Flatten all documents from all pages
  const documents = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page?.documents ?? []);
  }, [data]);

  // Get pagination from the last page (if available)
  const pagination = useMemo(() => {
    const pages = data?.pages;
    if (!pages || pages.length === 0) return undefined;
    return pages[pages.length - 1]?.pagination;
  }, [data]);

  return {
    documents,
    pagination,
    isLoading,
    isError,
    hasMore: hasNextPage,
    loadMore: fetchNextPage,
    isFetchingNextPage,
    error: error?.message ?? null,
  };
};
